import { useReducer, type Dispatch } from 'react';
import {
  DEFAULT_ANIMATION,
  DEFAULT_CONTROLS,
  DEFAULT_SOUND,
  defaultPresetFor,
  isOpenerVariant,
  setPath,
  type AnimationConfig,
  type AnimationOptions,
  type AnimationVariant,
  type ControlsConfig,
  type KnobValue,
  type SoundConfig,
} from '@game-platform/pack-animation';

export interface EditorState {
  animation: AnimationConfig;
  /** Something changed since load/save — gates the Save button. */
  dirty: boolean;
}

export type EditorAction =
  | { type: 'loaded'; animation: AnimationConfig }
  /** `animation` is what was sent: an edit made while the write was in flight stays dirty. */
  | { type: 'saved'; animation: AnimationConfig }
  | { type: 'set-variant'; variant: AnimationVariant }
  | { type: 'set-preset'; preset: string }
  | { type: 'set-knob'; path: string; value: KnobValue }
  | { type: 'reset-options' }
  | { type: 'set-sound'; sound: Partial<SoundConfig> }
  | { type: 'set-controls'; controls: Partial<ControlsConfig> };

/**
 * A fresh config for a mechanic: its default preset, nothing tuned. Sound and
 * the Rip button are the operator's calls about the ceremony, not about a
 * mechanic, so a library mechanic keeps whatever was decided; the slot lane
 * has neither to keep.
 */
export function freshConfig(
  variant: AnimationVariant,
  sound?: SoundConfig,
  controls?: ControlsConfig,
): AnimationConfig {
  return isOpenerVariant(variant)
    ? {
        variant,
        preset: defaultPresetFor(variant),
        ...(sound ? { sound } : {}),
        ...(controls ? { controls } : {}),
      }
    : { variant: 'slot' };
}

export function animationEditorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'loaded':
      return { animation: action.animation, dirty: false };
    case 'saved':
      return { ...state, dirty: state.animation !== action.animation };
    case 'set-variant':
      // Knobs of one mechanic mean nothing to another — start clean
      if (action.variant === state.animation.variant) return state;
      return {
        animation: freshConfig(action.variant, state.animation.sound, state.animation.controls),
        dirty: true,
      };
    case 'set-preset': {
      if (!isOpenerVariant(state.animation.variant)) return state;
      // A preset is a different set of numbers; a delta tuned against the old one no longer applies
      const { sound, controls } = state.animation;
      return {
        animation: {
          variant: state.animation.variant,
          preset: action.preset,
          ...(sound ? { sound } : {}),
          ...(controls ? { controls } : {}),
        },
        dirty: true,
      };
    }
    case 'set-sound': {
      if (!isOpenerVariant(state.animation.variant)) return state;
      const sound: SoundConfig = { ...(state.animation.sound ?? DEFAULT_SOUND), ...action.sound };
      return { animation: { ...state.animation, sound }, dirty: true };
    }
    case 'set-controls': {
      if (!isOpenerVariant(state.animation.variant)) return state;
      const controls: ControlsConfig = { ...(state.animation.controls ?? DEFAULT_CONTROLS), ...action.controls };
      return { animation: { ...state.animation, controls }, dirty: true };
    }
    case 'set-knob': {
      const options = setPath(
        (state.animation.options ?? {}) as Record<string, unknown>,
        action.path,
        action.value,
      ) as AnimationOptions;
      return { animation: { ...state.animation, options }, dirty: true };
    }
    case 'reset-options': {
      if (!state.animation.options) return state;
      const animation: AnimationConfig = { ...state.animation };
      delete animation.options;
      return { animation, dirty: true };
    }
  }
}

export const INITIAL_EDITOR_STATE: EditorState = { animation: DEFAULT_ANIMATION, dirty: false };

export function useAnimationEditor(initial?: AnimationConfig): [EditorState, Dispatch<EditorAction>] {
  return useReducer(
    animationEditorReducer,
    initial ? { animation: initial, dirty: false } : INITIAL_EDITOR_STATE,
  );
}
